import { useParams } from "react-router-dom";
import { useCallback } from "react";
import styled, { x } from "@xstyled/styled-components";

import { SourcingSideBar } from "../shared";

import { useSourcingPreferences } from "./useSourcingPreferences";
import { JobSubFunctionIdsField } from "./JobSubFunctionIdsField";
import { KeywordsField } from "./KeywordsField";
import { MaxSalaryField } from "./MaxSalaryField";
import { ExperienceRangeField } from "./ExperienceRangeField";
import { RegionsField } from "./RegionsField";
import { VisaSponsorshipRegionsField } from "./VisaSponsorshipRegionsField";
import { SavedJobAndCompanyFields } from "./SavedJobAndCompanyFields";
import { CandidateActivityField } from "./CandidateActivityField";
import { DiversityFields } from "./DiversityFields";
import { CompletedProfileField } from "./CompletedProfileField";
import { CompanyNamesField } from "./CompanyNamesField";
import { SubregionsField } from "./SubregionsField";

import { PersonalisedCandidatesPreferences } from "@hire/schema";
import { Loading } from "@otta/shared-components";
import { Spacing, Text, theme } from "@otta/design";
import { RectangleTag } from "@hire/components/tags/RectangleTag";
import {
  UpsellSourcingCTA,
  UpsellSourcingStatistics,
} from "@hire/pages/JobPage/Analytics/SourcingStatistics";

const Container = styled.div`
  padding-top: lg;
`;

export function SourcingPreferences(): React.ReactElement | null {
  const params = useParams<"jobId">();

  const jobId = params.jobId as string;

  const { preferences, setPreferences } = useSourcingPreferences(jobId, {
    fetchPolicy: "cache-only",
  });

  const handleSubmit = useCallback(
    (partialPreferences: Partial<PersonalisedCandidatesPreferences>) => {
      setPreferences({ ...preferences, ...partialPreferences });
    },
    [preferences, setPreferences]
  );

  return (
    <SourcingSideBar>
      <Container>
        {!preferences ? (
          <Loading />
        ) : (
          <Spacing size={1}>
            <JobSubFunctionIdsField
              preferences={preferences}
              onChange={handleSubmit}
            />
            <KeywordsField preferences={preferences} onChange={handleSubmit} />
            <CompanyNamesField
              preferences={preferences}
              onChange={handleSubmit}
            />
            <ExperienceRangeField
              preferences={preferences}
              onChange={handleSubmit}
            />
            <MaxSalaryField preferences={preferences} onChange={handleSubmit} />
            <RegionsField preferences={preferences} onChange={handleSubmit} />
            <SubregionsField
              preferences={preferences}
              onChange={handleSubmit}
            />
            <VisaSponsorshipRegionsField
              preferences={preferences}
              onChange={handleSubmit}
            />
            <CandidateActivityField
              preferences={preferences}
              onChange={handleSubmit}
            />
            <SavedJobAndCompanyFields
              preferences={preferences}
              onChange={handleSubmit}
            />
            <DiversityFields
              preferences={preferences}
              onChange={handleSubmit}
            />
            <CompletedProfileField
              preferences={preferences}
              onChange={handleSubmit}
            />
          </Spacing>
        )}
      </Container>
    </SourcingSideBar>
  );
}

export function UpsellSourcingPreferences() {
  return (
    <x.div p="xl" backgroundColor="white" h="100%">
      <x.div display="flex" flexDirection="column" gap="xl" maxW={1024}>
        <x.div display="flex" gap="sm" alignItems="center">
          <Text bold size={2}>
            Sourcing
          </Text>
          <RectangleTag
            icon="locked"
            color={theme.colors["yellow-500"]}
            iconColor={theme.colors.black}
            textColor={theme.colors.black}
            content="Upgrade"
          />
        </x.div>

        <UpsellSourcingCTA />
        <x.div display={{ _: "none", tablet: "block" }}>
          <UpsellSourcingStatistics />
        </x.div>
      </x.div>
    </x.div>
  );
}
